/**
 * Patrimônio compartilhado (ADR 0031, Facilities, Entrega 2): cadastro único de bens para
 * Facilities, TI e Controladoria, com posse, empresas proprietária e usuária, local físico,
 * movimentações, valores, termos de responsabilidade, inventário e etiquetas com QR Code.
 */
import { z } from 'zod';
import { defineFormSchemas, formFieldVisible, validateFormFieldValues } from './forms.ts';
import { localizedText } from './schemas.ts';

/** Domínio operacional do bem: quem o administra. */
export const ASSET_DOMAINS = ['facilities', 'it'] as const;
export type AssetDomain = (typeof ASSET_DOMAINS)[number];

/**
 * Tipo de posse. Só "own" tem empresa proprietária do grupo; os demais pertencem a um parceiro
 * (locador, comodante, cedente ou dono do bem sob guarda) e são devolvidos, nunca baixados.
 */
export const ASSET_OWNERSHIP = [
  'own',
  'leased',
  'third_party_loan',
  'comodato_received',
  'ceded_to_group',
  'third_party_custody',
] as const;
export type AssetOwnership = (typeof ASSET_OWNERSHIP)[number];

export const ASSET_STATUSES = [
  'in_stock',
  'in_use',
  'in_maintenance',
  'with_third_party',
  'missing',
  'disposed',
  'returned_to_owner',
] as const;
export type AssetStatus = (typeof ASSET_STATUSES)[number];
/** Situações finais: o bem não se movimenta mais. */
export const ASSET_FINAL_STATUSES: readonly AssetStatus[] = ['disposed', 'returned_to_owner'];

export const ASSET_CONDITIONS = ['new', 'good', 'fair', 'poor', 'unusable'] as const;
export const ASSET_CRITICALITY = ['low', 'medium', 'high', 'critical'] as const;

/**
 * Movimentações. Local, custodiante, empresa usuária e situação só mudam por elas; as marcadas
 * com aprovação só são aplicadas quando o motor de alçadas aprova.
 */
export const ASSET_MOVEMENT_KINDS = [
  'registration',
  'transfer',
  'custody_assign',
  'custody_return',
  'maintenance_in',
  'maintenance_out',
  'third_party_out',
  'third_party_return',
  'intercompany_use',
  'intercompany_return',
  'missing',
  'found',
  'return_to_owner',
  'disposal',
  'inventory_adjustment',
] as const;
export type AssetMovementKind = (typeof ASSET_MOVEMENT_KINDS)[number];
/** Movimentações que a equipe registra direto (as demais nascem de aprovação ou inventário). */
export const ASSET_DIRECT_MOVEMENTS: readonly AssetMovementKind[] = [
  'transfer',
  'custody_assign',
  'custody_return',
  'maintenance_in',
  'maintenance_out',
  'third_party_out',
  'third_party_return',
  'intercompany_return',
  'missing',
  'found',
  'return_to_owner',
];

/** Pedidos que passam pelo motor de alçadas. */
export const ASSET_REQUEST_KINDS = ['disposal', 'intercompany_use', 'term_waiver'] as const;
export type AssetRequestKind = (typeof ASSET_REQUEST_KINDS)[number];
export const ASSET_DISPOSAL_REASONS = [
  'obsolete',
  'unusable',
  'missing',
  'stolen',
  'sold',
  'donated',
  'scrapped',
] as const;

export const ASSET_TERM_STATUSES = [
  'pending',
  'formalized',
  'waiver_pending',
  'waived',
  'superseded',
] as const;

/**
 * Política do valor de referência para aprovar baixas e cessões (ADR 0031, D1). "undefined":
 * enquanto a política patrimonial não for definida, esses pedidos não são enviados.
 */
export const ASSET_REFERENCE_RULES = [
  'undefined',
  'net_book_value',
  'estimated_value',
  'acquisition_value',
  'highest_available',
] as const;
export type AssetReferenceRule = (typeof ASSET_REFERENCE_RULES)[number];

type Money = { amount: string | null; currency: string | null };
export interface AssetValues {
  acquisition: Money;
  /** Valor contábil líquido validado pela Controladoria (só conta se validado). */
  netBook: Money & { validated: boolean };
  estimated: Money;
}

/**
 * Valor de referência conforme a política. Sem política, ou sem o valor exigido, não há valor:
 * o pedido não pode ser enviado (nunca se usa o valor histórico por padrão). "highest_available"
 * compara só valores na mesma moeda; com moedas diferentes, exige decisão (sem valor).
 */
export function assetReferenceValue(
  v: AssetValues,
  rule: AssetReferenceRule,
):
  | {
      amount: string;
      currency: string;
      basis: Exclude<AssetReferenceRule, 'undefined' | 'highest_available'>;
    }
  | { amount: null; reason: 'policy_undefined' | 'value_missing' | 'mixed_currencies' } {
  const ok = (m: Money) => m.amount != null && m.currency != null;
  const pick = (basis: 'net_book_value' | 'estimated_value' | 'acquisition_value') => {
    const m =
      basis === 'net_book_value'
        ? v.netBook
        : basis === 'estimated_value'
          ? v.estimated
          : v.acquisition;
    if (basis === 'net_book_value' && !v.netBook.validated) return null;
    return ok(m) ? { amount: m.amount!, currency: m.currency!, basis } : null;
  };
  if (rule === 'undefined') return { amount: null, reason: 'policy_undefined' };
  if (rule !== 'highest_available') return pick(rule) ?? { amount: null, reason: 'value_missing' };
  const all = (['net_book_value', 'estimated_value', 'acquisition_value'] as const)
    .map(pick)
    .filter((x): x is NonNullable<typeof x> => !!x);
  if (all.length === 0) return { amount: null, reason: 'value_missing' };
  if (new Set(all.map((x) => x.currency)).size > 1)
    return { amount: null, reason: 'mixed_currencies' };
  const toCents = (s: string) => {
    const [i, d = ''] = s.split('.');
    return BigInt(i!) * 10000n + BigInt((d + '0000').slice(0, 4));
  };
  return all.reduce((a, b) => (toCents(b.amount) > toCents(a.amount) ? b : a));
}

/** Etiquetas: tamanhos disponíveis (a arquitetura aceita novos formatos). Medidas em mm. */
export interface LabelLayout {
  key: string;
  label: { 'pt-BR': string; en: string };
  page: 'A4' | 'single';
  width: number;
  height: number;
  columns: number;
  rows: number;
  marginTop: number;
  marginLeft: number;
  gapX: number;
  gapY: number;
}
export const LABEL_LAYOUTS: readonly LabelLayout[] = [
  {
    key: 'a4-3x8',
    label: {
      'pt-BR': 'Folha A4: 24 etiquetas (3 × 8, 63,5 × 33,9 mm)',
      en: 'A4 sheet: 24 labels (3 × 8, 63.5 × 33.9 mm)',
    },
    page: 'A4',
    width: 63.5,
    height: 33.9,
    columns: 3,
    rows: 8,
    marginTop: 13,
    marginLeft: 7.2,
    gapX: 2.5,
    gapY: 0,
  },
  {
    key: 'a4-2x7',
    label: {
      'pt-BR': 'Folha A4: 14 etiquetas (2 × 7, 99,1 × 38,1 mm)',
      en: 'A4 sheet: 14 labels (2 × 7, 99.1 × 38.1 mm)',
    },
    page: 'A4',
    width: 99.1,
    height: 38.1,
    columns: 2,
    rows: 7,
    marginTop: 15.1,
    marginLeft: 4.7,
    gapX: 2.5,
    gapY: 0,
  },
  {
    key: 'single-60x40',
    label: { 'pt-BR': 'Etiqueta individual (60 × 40 mm)', en: 'Single label (60 × 40 mm)' },
    page: 'single',
    width: 60,
    height: 40,
    columns: 1,
    rows: 1,
    marginTop: 0,
    marginLeft: 0,
    gapX: 0,
    gapY: 0,
  },
  // Etiqueta pequena usada hoje por Facilities (avaliação funcional, item 6).
  {
    key: 'single-45x15',
    label: {
      'pt-BR': 'Etiqueta individual pequena (45 × 15 mm)',
      en: 'Small single label (45 × 15 mm)',
    },
    page: 'single',
    width: 45,
    height: 15,
    columns: 1,
    rows: 1,
    marginTop: 0,
    marginLeft: 0,
    gapX: 0,
    gapY: 0,
  },
];

/** Número de patrimônio: letras, números, ponto, hífen ou barra; guardado em maiúsculas. */
export const ASSET_TAG_RE = /^[A-Z0-9][A-Z0-9./-]{0,39}$/;
export function normalizeTag(tag: string): string {
  return tag.trim().toUpperCase().replace(/\s+/g, '');
}

// ----- Campos específicos por classe (mecanismo compartilhado de formulários) -----

export const ASSET_FIELD_TYPES = [
  'text',
  'long_text',
  'number',
  'date',
  'money',
  'select',
  'multi_select',
  'checkbox',
] as const;
const assetForms = defineFormSchemas(ASSET_FIELD_TYPES);
export const assetClassSchemaInput = assetForms.schema;
export type AssetClassSchema = z.infer<typeof assetClassSchemaInput>;
export function validateAssetFieldValues(
  schema: AssetClassSchema,
  values: Record<string, unknown>,
) {
  return validateFormFieldValues(schema, values);
}
export const assetFieldVisible = formFieldVisible;

// ----- Entradas -----

const optional = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional();
const optionalId = z
  .uuid()
  .or(z.literal(''))
  .transform((v) => (v === '' ? null : v))
  .nullable()
  .optional();
const optionalDate = z
  .string()
  .regex(/^\d{4}-\d{2}-\d{2}$/)
  .or(z.literal(''))
  .transform((v) => (v === '' ? null : v))
  .nullable()
  .optional();
const decimal = z
  .string()
  .trim()
  .regex(/^(\d{1,15}(\.\d{1,4})?)?$/, 'Use números, com ponto nos centavos')
  .transform((v) => (v === '' ? null : v))
  .nullable()
  .optional();
const currency = z
  .string()
  .regex(/^([A-Z]{3})?$/)
  .transform((v) => (v === '' ? null : v))
  .nullable()
  .optional();
const pair =
  <T extends { [k: string]: unknown }>(a: keyof T, b: keyof T) =>
  (v: T) =>
    (v[a] == null) === (v[b] == null);

export const assetClassInput = z.object({
  code: z
    .string()
    .trim()
    .toUpperCase()
    .regex(/^[A-Z0-9][A-Z0-9_.-]{0,29}$/, 'Use letras, números, ponto, hífen ou _'),
  name: localizedText,
  description: z
    .object({ 'pt-BR': z.string().trim().max(1000), en: z.string().trim().max(1000) })
    .default({ 'pt-BR': '', en: '' }),
  parentId: optionalId,
  domain: z.enum(ASSET_DOMAINS),
  accountingGroup: optional(100),
  usefulLifeMonths: z.number().int().min(1).max(1200).nullable().optional(),
  requiresSerial: z.boolean().default(false),
  requiresTerm: z.boolean().default(false),
  defaultCriticality: z.enum(ASSET_CRITICALITY).default('low'),
  schema: assetClassSchemaInput.default({ fields: [] }),
  isActive: z.boolean().default(true),
  sortOrder: z.number().int().min(1).max(999).default(100),
});
export type AssetClassInput = z.infer<typeof assetClassInput>;

/** Dados do bem que a equipe do domínio mantém (local, custodiante e situação vêm de movimentações). */
export const assetDataInput = z
  .object({
    classId: z.uuid(),
    description: z.string().trim().min(1).max(300),
    brand: optional(120),
    model: optional(120),
    serialNumber: optional(120),
    /** Nº do patrimônio antigo (etiqueta anterior), em texto: zeros à esquerda ficam. */
    legacyTag: z
      .string()
      .trim()
      .max(60)
      // eslint-disable-next-line no-control-regex
      .regex(/^[^\u0000-\u001f\u007f]*$/, 'Sem caracteres de controle')
      .transform((v) => (v === '' ? null : v))
      .nullable()
      .optional(),
    parentAssetId: optionalId,
    ownership: z.enum(ASSET_OWNERSHIP),
    ownerEntityId: optionalId,
    thirdPartyOwnerId: optionalId,
    agreementContractId: optionalId,
    agreementUntil: optionalDate,
    agreementAmount: decimal,
    agreementCurrency: currency,
    costCenterId: optionalId,
    departmentId: optionalId,
    condition: z.enum(ASSET_CONDITIONS).default('good'),
    criticality: z.enum(ASSET_CRITICALITY).default('low'),
    acquisitionDate: optionalDate,
    acquisitionValue: decimal,
    acquisitionCurrency: currency,
    invoiceNumber: optional(60),
    supplierId: optionalId,
    purchaseRequestId: optionalId,
    warrantyUntil: optionalDate,
    warrantyPartnerId: optionalId,
    warrantyNotes: optional(1000),
    manufacturer: optional(120),
    servicePartnerId: optionalId,
    estimatedValue: decimal,
    estimatedCurrency: currency,
    estimatedOn: optionalDate,
    estimatedJustification: optional(1000),
    notes: optional(2000),
    fieldValues: z.record(z.string(), z.unknown()).default({}),
  })
  .refine((v) => (v.ownership === 'own') === !!v.ownerEntityId, {
    path: ['ownerEntityId'],
    message: 'Bem próprio tem empresa proprietária; bem de terceiro, não',
  })
  .refine((v) => v.ownership === 'own' || !!v.thirdPartyOwnerId, {
    path: ['thirdPartyOwnerId'],
    message: 'Informe o dono do bem (parceiro)',
  })
  .refine(pair('acquisitionValue', 'acquisitionCurrency'), {
    path: ['acquisitionCurrency'],
    message: 'Informe o valor e a moeda juntos',
  })
  .refine(pair('agreementAmount', 'agreementCurrency'), {
    path: ['agreementCurrency'],
    message: 'Informe o valor e a moeda juntos',
  })
  .refine((v) => (v.estimatedValue == null) === (v.estimatedCurrency == null), {
    path: ['estimatedCurrency'],
    message: 'Informe o valor e a moeda juntos',
  })
  .refine((v) => v.estimatedValue == null || (!!v.estimatedOn && !!v.estimatedJustification), {
    path: ['estimatedJustification'],
    message: 'Valor estimado exige data e justificativa',
  });
export type AssetDataInput = z.infer<typeof assetDataInput>;

/** Cadastro: os dados, o número (vazio = automático) e onde o bem começa. */
export const assetCreateInput = z.object({
  tag: z
    .string()
    .trim()
    .max(40)
    .transform((v) => (v === '' ? null : normalizeTag(v)))
    .refine((v) => v == null || ASSET_TAG_RE.test(v), 'Número de patrimônio inválido')
    .nullable()
    .optional(),
  userEntityId: z.uuid(),
  locationId: z.uuid(),
  custodianUserId: optionalId,
  /** Colaborador que recebe o bem (com ou sem login, ADR 0085). */
  custodianEmployeeId: optionalId,
  data: assetDataInput,
});

/** Dados contábeis (só a Controladoria). */
export const assetAccountingInput = z
  .object({
    isFixedAsset: z.boolean(),
    accountingGroup: optional(100),
    netsuiteCode: optional(60),
    netBookValue: decimal,
    netBookCurrency: currency,
    netBookAsOf: optionalDate,
  })
  .refine((v) => (v.netBookValue == null) === (v.netBookCurrency == null), {
    path: ['netBookCurrency'],
    message: 'Informe o valor e a moeda juntos',
  })
  .refine((v) => v.netBookValue == null || !!v.netBookAsOf, {
    path: ['netBookAsOf'],
    message: 'Informe a data do valor contábil',
  });

export const assetMovementInput = z
  .object({
    kind: z.enum(ASSET_MOVEMENT_KINDS),
    effectiveDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    toLocationId: optionalId,
    toCustodianUserId: optionalId,
    toCustodianEmployeeId: optionalId,
    counterpartyId: optionalId,
    returnDue: optionalDate,
    reason: z.string().trim().min(5).max(2000),
    facilitiesRequestId: optionalId,
  })
  .refine((v) => v.kind !== 'transfer' || !!v.toLocationId, {
    path: ['toLocationId'],
    message: 'Informe o local de destino',
  })
  .refine(
    (v) => v.kind !== 'custody_assign' || !!v.toCustodianUserId || !!v.toCustodianEmployeeId,
    {
      path: ['toCustodianEmployeeId'],
      message: 'Informe o custodiante',
    },
  )
  .refine((v) => v.kind !== 'third_party_out' || (!!v.counterpartyId && !!v.returnDue), {
    path: ['counterpartyId'],
    message: 'Informe o parceiro e o prazo de retorno',
  });
export type AssetMovementInput = z.infer<typeof assetMovementInput>;

export const assetDisposalInput = z.object({
  reason: z.enum(ASSET_DISPOSAL_REASONS),
  destination: optional(500),
  justification: z.string().trim().min(5).max(2000),
  costCenterId: z.uuid(),
});
export const assetIntercompanyInput = z.object({
  toEntityId: z.uuid(),
  toLocationId: z.uuid(),
  toCustodianUserId: optionalId,
  toCustodianEmployeeId: optionalId,
  returnDue: optionalDate,
  justification: z.string().trim().min(5).max(2000),
  costCenterId: z.uuid(),
  legalRequestId: optionalId,
  accountingNote: optional(2000),
});
export const assetTermWaiverInput = z.object({
  justification: z.string().trim().min(5).max(2000),
  costCenterId: z.uuid(),
});
export const assetTermLinkInput = z.object({ legalRequestId: z.uuid() });
export const assetPolicyInput = z.object({ disposalReferenceRule: z.enum(ASSET_REFERENCE_RULES) });

export const assetInventoryInput = z.object({
  legalEntityId: z.uuid(),
  branchId: optionalId,
  title: z.string().trim().min(1).max(150),
  notes: optional(2000),
});
export const assetCountInput = z.object({
  tag: z.string().trim().min(1).max(40),
  foundLocationId: z.uuid(),
  condition: z.enum(ASSET_CONDITIONS).default('good'),
  note: optional(1000),
});
/** Fechamento: decisão sobre cada divergência, com justificativa. */
export const assetInventoryCloseInput = z.object({
  decisions: z
    .array(
      z.object({
        assetId: z.uuid(),
        action: z.enum(['relocate', 'missing', 'keep']),
        justification: z.string().trim().min(5).max(1000),
      }),
    )
    .max(5000),
});
